import React, { useRef } from "react";
import { Grid } from "@mui/material";
import { useInBodyContext } from "@icoach/inbody/components/InBodyContext";
import {
	AgreedNotesBox,
	GuidanceNotesBox,
	HealthDeclarationBox,
	HundredTShirtSizeBox,
	MeasureDayBox,
	SalesPushBox,
	SpecialBodyConditionBox,
	TodoCard,
	XFactorNotesBox,
	MarketingBox
} from "@icoach/memberDetail/components";
import { Card, Typography } from "@curves/ui-base";
import { QUERY_KEYS } from "@icoach/constants";

const InBodyInterviewMemberStateSummary = React.forwardRef(({ sourceData: memberProfile }, ref) => {
	const {
		resource,
		serviceTasks,
		measureDay,
		isInterviewNotMeasure,
		proteinPush,
		probioticsPush,
		calciumPush,
		smartRingPush,
		hundredTShirtSize,
		xFactorNotes = [],
		agreedNotes = [],
		healthDeclaration,
		guidanceNotes,
		specialBodyCondition,
        hasMarketing,
	} = memberProfile || {};

	const {
		peopleID: memberID,
		dialogRef,
		updateGuidanceNotes,
		updateSpecialBodyCondition,
		updateAgreedNotes,
		updateXFactorNotes,
		updateMeasureDay,
		updateProteinPush,
		updateProbioticsPush,
		updateCalciumPush,
		updateSmartRingPush,
		updateHundredTShirtSize,
		updateServiceTasks,
        updateMarketingStatus
	} = useInBodyContext();

	const { serviceTodoTextOptions = [] } = resource;

	const todoRef = useRef(null);

	return (
		<Grid container spacing={2}>
			<Grid xs={4} item>
				<Card className="p-2">
					<Typography variant="body1" className="font-weight-bold">
						健康與指導
					</Typography>
					<Card.CardContent>
						<HealthDeclarationBox
							memberID={memberID}
							membershipStatus={memberProfile?.membershipStatus}
							healthDeclaration={healthDeclaration}
							healthDeclarationRedirectFrom={QUERY_KEYS.IN_BODY_INTERVIEW}
							className="mb-2"
						/>
						<GuidanceNotesBox
							guidanceNotes={guidanceNotes}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openGuidanceNotesDialog(
									{
										guidanceNotes,
										memberID,
									},
									updateGuidanceNotes
								)
							}
							className="mb-2"
						/>
						<SpecialBodyConditionBox
							specialBodyCondition={specialBodyCondition}
							onEdit={() => {
								dialogRef.current &&
									dialogRef.current.openSpecialBodyConditionDialog(
										{
											specialBodyCondition,
											memberID,
										},
										updateSpecialBodyCondition
									);
							}}
						/>
					</Card.CardContent>
				</Card>
			</Grid>
			<Grid xs={4} item>
				<Card className="p-2">
					<Typography variant="body1" className="font-weight-bold">
						X-Factor 與約定
					</Typography>
					<Card.CardContent>
						<XFactorNotesBox
							xFactorNotes={xFactorNotes}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current?.openXFactorDialog(
									{
										xFactorNotes,
										memberID,
									},
									updateXFactorNotes
								)
							}
							className="mb-2"
						/>
						<AgreedNotesBox
							agreedNotes={agreedNotes}
							memberID={memberID}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openAgreedNotesDialog(
									{
										agreedNotes,
										memberID,
									},
									updateAgreedNotes
								)
							}
						/>
					</Card.CardContent>
				</Card>
			</Grid>
			<Grid xs={4} item>
				<Card className="p-2 mb-2">
					<Typography variant="body1" className="font-weight-bold">
						會員狀態
					</Typography>
					<Card.CardContent>
						<MeasureDayBox
							sourceData={memberProfile}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openMeasureDayDialog(
									{
										measureDay,
										isInterviewNotMeasure,
										showInterviewNotMeasureIcon: false,
										memberID,
									},
									updateMeasureDay
								)
							}
						/>
						<MarketingBox
							hasMarketing={hasMarketing}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openMarketingDialog(
									{
										hasMarketing: hasMarketing,
										memberID,
									},
									updateMarketingStatus
								)
							}
						/>
						<SalesPushBox
							label="蛋白訂單"
							sourceData={proteinPush}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openProteinPushDialog(
									{
										proteinPush,
										memberID,
										...resource,
									},
									updateProteinPush
								)
							}
						/>
						<SalesPushBox
							label="益菌訂單"
							sourceData={probioticsPush}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openProbioticsPushDialog(
									{
										probioticsPush,
										memberID,
										...resource,
									},
									updateProbioticsPush
								)
							}
						/>
						<SalesPushBox
							label="鈣訂單"
							sourceData={calciumPush}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openCalciumPushDialog(
									{
										calciumPush,
										memberID,
										...resource,
									},
									updateCalciumPush
								)
							}
						/>
						<SalesPushBox
							label="智能系統"
							sourceData={smartRingPush}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openSmartRingPushDialog(
									{
										smartRingPush,
										memberID,
										...resource,
									},
									updateSmartRingPush
								)
							}
						/>
						<HundredTShirtSizeBox
							hundredTShirtSize={hundredTShirtSize}
							onEdit={() =>
								dialogRef.current &&
								dialogRef.current.openHundredTShirtSizeDialog(
									{
										hundredTShirtSize,
										options: resource.hundredTShirtSizeTextOptions,
										memberID,
									},
									updateHundredTShirtSize
								)
							}
						/>
					</Card.CardContent>
				</Card>
				<TodoCard
					todoRef={todoRef}
					memberID={memberID}
					todos={serviceTasks}
					options={serviceTodoTextOptions}
					onClick={() =>
						dialogRef.current &&
						dialogRef.current.openTodoDialog(
							{
								todos: (todoRef.current && todoRef.current.getTodos()) || serviceTasks,
								memberID,
								options: serviceTodoTextOptions,
							},
							updateServiceTasks
						)
					}
				/>
			</Grid>
		</Grid>
	);
});

export default InBodyInterviewMemberStateSummary;
